import type { StoreStockStatusFragment, TtLocationFragment } from '#graphql-operations'

export type Coords = {
  lat: number
  lng: number
}

export default function useStorelocator() {
  const { cart, items, getCartId, setCartData, createEmptyCart } = useCart()
  const lastResult = ref<any>()
  const search = ref('')
  const userLocation = ref<Coords | null>()
  const hasSearched = ref(false)
  const isSearching = ref(false)

  // store_stock is fetched separately from the cart (and its items) because it is
  // expensive to resolve. Loading it as part of the cart fragment slows down every
  // cart mutation (e.g. the checkout step 1 -> step 2 transition). Instead it is
  // loaded on-demand via `fetchStoreStock()` on the pages that actually need it
  // (the cart page and checkout step 2).
  const cartStoreStockData = useState<{ store_stock?: StoreStockStatusFragment | null, items: Record<string, StoreStockStatusFragment | null | undefined> } | null>('cartStoreStockData', () => null)
  const cartStoreStockCartId = useState<string | null>('cartStoreStockCartId', () => null)
  // Signature of the distinct SKUs currently in the cart. We only care whether the set
  // of products in the cart has changed (e.g. an item was added/removed), not how many of
  // each are in there, so quantity changes don't trigger an unnecessary refetch.
  const cartStoreStockSkus = useState<string | null>('cartStoreStockSkus', () => null)
  const isFetchingStoreStock = useState<boolean>('cartStoreStockLoading', () => false)

  const cartSkuSignature = computed<string>(() => {
    const skus = (items.value || [])
      .map(item => item?.product?.sku ? `${item.product.sku}-${item.quantity}` : undefined)
      .filter((sku): sku is string => !!sku)
    return Array.from(new Set(skus)).sort().join(',')
  })

  const allocatedStore = computed<TtLocationFragment | null>(() => {
    if (cart.value?.store_allocation) {
      return cart.value.store_allocation as TtLocationFragment
    }
    return null
  })

  const allLocations = computed<TtLocationFragment[]>(() => lastResult.value?.ttLocations?.locations || [])
  const visibleLocations = computed<TtLocationFragment[]>(() => allLocations.value.filter(location => location.storeLocator))
  const pickupLocations = computed<TtLocationFragment[]>(() => visibleLocations.value.filter(location => location.pickupInStore))
  const metadata = computed(() => lastResult.value?.ttLocations?.metadata || {})

  const searchInput = computed(() => {
    return {
      geolocation: search.value
        ? {
            country: 'nl',
            search: search.value,
          }
        : undefined,
      latlng: !search.value && userLocation.value
        ? {
            latitude: userLocation.value.lat,
            longitude: userLocation.value.lng,
          }
        : undefined,
    }
  })

  const doSearch = async (input: any | undefined) => {
    try {
      isSearching.value = true
      const response = await useGraphqlQuery('storeSearch', { input: input || {} }, useGraphqlDefaultClient())
      if (input) {
        hasSearched.value = true
      }
      lastResult.value = response.data
    }
    catch (error) {
      console.error('Error searching stores:', error)
    }
    finally {
      isSearching.value = false
    }
  }

  const getMyLocation = async () => {
    if (navigator?.geolocation) {
      userLocation.value = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(
              position.coords
                ? {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  }
                : null,
            )
          },
          (error) => {
            console.error('Error getting location:', error)
            reject(error)
          },
        )
      })

      await nextTick(() => {
        search.value = ''
        doSearch(searchInput.value)
      })
    }
  }

  const storeStock = computed<StoreStockStatusFragment | undefined>(() => {
    return cartStoreStockData.value?.store_stock || undefined
  })

  const getItemStoreStock = (itemId: string): StoreStockStatusFragment | undefined => {
    return cartStoreStockData.value?.items?.[itemId] || undefined
  }

  const fetchStoreStock = async (force = false) => {
    if (!getCartId.value || isFetchingStoreStock.value) {
      return
    }

    if (
      !force
      && cartStoreStockCartId.value === getCartId.value
      && cartStoreStockSkus.value === cartSkuSignature.value
      && cartStoreStockData.value
    ) {
      return
    }

    isFetchingStoreStock.value = true

    try {
      const response = await useGraphqlQuery('getCartStoreStock', { cartId: getCartId.value }, useGraphqlCustomerClient())
      const cartData = response?.data?.cart

      if (cartData) {
        cartStoreStockData.value = {
          store_stock: cartData.store_stock || null,
          items: (cartData.items || []).reduce<Record<string, StoreStockStatusFragment | null | undefined>>((acc, item) => {
            if (item?.id) {
              acc[item.id] = item.store_stock || null
            }
            return acc
          }, {}),
        }
        cartStoreStockCartId.value = getCartId.value
        cartStoreStockSkus.value = cartSkuSignature.value
      }
    }
    catch (error) {
      console.error('Error fetching cart store stock:', error)
    }
    finally {
      isFetchingStoreStock.value = false
    }
  }

  const qtyStoresWithStock = computed<number>(() => {
    return storeStock.value?.stores?.filter(item => item?.status === 'in-stock').length || 0
  })

  const qtyStoresWithStockOrBackorder = computed<number>(() => {
    return storeStock.value?.stores?.filter(item => item?.status === 'in-stock' || item?.status?.includes('backorder')).length || 0
  })

  const setAllocationStore = async (store: TtLocationFragment | null) => {
    if (!cart.value) {
      await createEmptyCart()
    }

    const result = await useGraphqlMutation('setStoreAllocationOnCart', {
      input: {
        cart_id: getCartId.value,
        store_code: store ? String(store.code) : null,
      },
    }, useGraphqlCustomerClient())

    setCartData(result?.data?.setStoreAllocationOnCart?.cart)
  }

  return {
    allocatedStore,
    allLocations,
    visibleLocations,
    pickupLocations,
    metadata,
    search,
    searchInput,
    userLocation,
    hasSearched,
    isSearching,
    doSearch,
    getMyLocation,
    setAllocationStore,
    qtyStoresWithStock,
    qtyStoresWithStockOrBackorder,
    storeStock,
    fetchStoreStock,
    getItemStoreStock,
    cartSkuSignature,
  }
}
